import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Settings } from "lucide-react";

import { AdminLoginCard } from "@/components/admin/AdminLoginCard";
import { SelmaAdminMobileNav, SelmaAdminSidebar } from "@/components/admin/SelmaAdminSidebar";
import { Button } from "@/components/ui/button";
import { adminFetch, useAdminSession } from "@/hooks/use-admin-session";

// DuyguŞifa "Selma" admin paneli — "Ayarlar" sayfası. Şu an tek amacı: "Ödeme
// Linki Gönder" mailinin içindeki IBAN/hesap sahibi/banka adı bilgilerini admin
// panelinden düzenlenebilir kılmak (env değişkeni DEĞİL — bkz. CLAUDE.md →
// "Talepler" bölümü). duygusifa_odeme_ayarlari tek satırlık bir settings tablosu.

type OdemeAyarlari = {
  id: string;
  iban: string | null;
  hesap_sahibi: string | null;
  banka_adi: string | null;
  updated_at: string;
};

async function fetchAyarlar(): Promise<OdemeAyarlari | null> {
  const res = await adminFetch("/api/admin/selma/odeme-ayarlari");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(json?.error ?? "Ayarlar alınamadı.");
  return json?.data ?? null;
}

async function saveAyarlar(payload: { iban: string; hesap_sahibi: string; banka_adi: string }): Promise<OdemeAyarlari> {
  const res = await adminFetch("/api/admin/selma/odeme-ayarlari", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(json?.error ?? "Kaydedilemedi.");
  return json.data;
}

export default function AdminSelmaAyarlar() {
  const queryClient = useQueryClient();
  const { sessionQuery, loginMutation, logoutMutation } = useAdminSession();
  const configured = Boolean(sessionQuery.data?.configured);
  const authenticated = Boolean(sessionQuery.data?.authenticated);

  const [iban, setIban] = useState("");
  const [hesapSahibi, setHesapSahibi] = useState("");
  const [bankaAdi, setBankaAdi] = useState("");
  const [saved, setSaved] = useState(false);

  const ayarlarQuery = useQuery({
    queryKey: ["selma-odeme-ayarlari"],
    queryFn: fetchAyarlar,
    enabled: authenticated,
    staleTime: 0,
  });

  useEffect(() => {
    if (ayarlarQuery.data) {
      setIban(ayarlarQuery.data.iban ?? "");
      setHesapSahibi(ayarlarQuery.data.hesap_sahibi ?? "");
      setBankaAdi(ayarlarQuery.data.banka_adi ?? "");
    }
  }, [ayarlarQuery.data]);

  const saveMutation = useMutation({
    mutationFn: saveAyarlar,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["selma-odeme-ayarlari"], exact: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    },
  });

  if (sessionQuery.isLoading) {
    return <div className="p-8 text-sm text-slate-500">Yükleniyor...</div>;
  }

  if (!authenticated) {
    return (
      <AdminLoginCard
        configured={configured}
        onLogin={(args) => loginMutation.mutate(args)}
        isLoading={loginMutation.isPending}
        error={loginMutation.isError ? (loginMutation.error as Error).message : undefined}
      />
    );
  }

  return (
    <div className="flex min-h-dvh w-full bg-slate-100/60">
      <SelmaAdminSidebar />

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="border-b border-slate-200 bg-white/80 px-4 py-3 backdrop-blur sm:px-6">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Settings className="h-4 w-4 text-slate-500" />
              <div className="text-base font-semibold text-slate-900 sm:text-lg">Selma — Ayarlar</div>
            </div>
            <Button variant="outline" size="sm" className="rounded-full" onClick={() => logoutMutation.mutate()} disabled={logoutMutation.isPending}>
              Çıkış
            </Button>
          </div>
          <div className="mt-3">
            <SelmaAdminMobileNav />
          </div>
        </header>

        <div className="min-w-0 flex-1 p-4 sm:p-6">
          {ayarlarQuery.isLoading ? (
            <div className="text-sm text-slate-500">Yükleniyor...</div>
          ) : ayarlarQuery.isError ? (
            <div className="text-sm text-destructive">{(ayarlarQuery.error as Error).message}</div>
          ) : (
            <div className="max-w-lg rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="text-2xl font-extrabold tracking-tight text-slate-900">Ödeme Bilgileri</div>
              <div className="mt-1 text-sm text-slate-500">
                "Talepler" sayfasındaki "Ödeme Linki Gönder" mailinde kullanıcıya gösterilen havale/EFT bilgileri.
              </div>

              <form
                className="mt-6 grid gap-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  saveMutation.mutate({ iban: iban.trim(), hesap_sahibi: hesapSahibi.trim(), banka_adi: bankaAdi.trim() });
                }}
              >
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">IBAN</label>
                  <input
                    className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-emerald-500/30"
                    value={iban}
                    onChange={(e) => setIban(e.target.value)}
                    placeholder="TR00 0000 0000 0000 0000 0000 00"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Hesap Sahibi</label>
                  <input
                    className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-emerald-500/30"
                    value={hesapSahibi}
                    onChange={(e) => setHesapSahibi(e.target.value)}
                    placeholder="Ad Soyad / Şirket Unvanı"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Banka Adı</label>
                  <input
                    className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-emerald-500/30"
                    value={bankaAdi}
                    onChange={(e) => setBankaAdi(e.target.value)}
                    placeholder="örn: Garanti BBVA"
                  />
                </div>

                {saveMutation.isError && (
                  <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                    {(saveMutation.error as Error).message}
                  </div>
                )}
                {saved && (
                  <div className="rounded-xl border border-green-500/30 bg-green-500/10 p-3 text-sm text-green-700">Kaydedildi.</div>
                )}

                <div>
                  <Button type="submit" size="sm" className="rounded-full" disabled={saveMutation.isPending}>
                    {saveMutation.isPending ? "Kaydediliyor…" : "Kaydet"}
                  </Button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
